import { Map as ImmutableMap } from 'immutable';
import { SIMULATE_FLOW_QUERY, SIMULATE_FLOW_QUERY_FIELDNAME } from 'automation-ui-canvas/containers/flow-views/test-flow/simulateFlowQuery';
import { useLazyQuery } from 'data-fetching-client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { RECORD_ID_QUERY_PARAM } from 'automation-ui-canvas/containers/flow-views/test-flow/constants';
import I18n from 'I18n';
import { mapTriggerAndEnrollmentChecksToValidationCheckResults } from 'flow-doctor-lib/utils/nonEnrollmentDiagnosis';
import { SIMULATE_ENROLLMENT_QUERY, SIMULATE_ENROLLMENT_QUERY_FIELDNAME } from 'automation-ui-canvas/containers/flow-views/test-flow/simulateEnrollmentQuery';
import useGetFlowAlerts from 'automation-ui-canvas/containers/flow-views/test-flow/useGetFlowAlerts';
import Action from 'automation-ui-action-features/records/Action';
import { getFollowedBranchIndex } from 'automation-ui-log-events/records/LogEvent/utils';
import LogEvent from 'automation-ui-log-events/records/LogEvent/LogEvent';
const VALIDATION_CHECKS_BLOCK_LIST = ['FLOW_DISABLED', 'SUPPRESSION_REMOVE_LIST_EVALUATION', 'EVENT_ENROLLMENT', 'FLOW_SCHEDULE_ENABLED', 'FLOW_ENROLLMENT_MERGE_ALLOWED'];
const TRIGGER_VALIDATION_CHECKS_BLOCK_LIST = ['MANUAL_ENROLLMENT'];
const useGetTestFlowData = ({
  flow
}) => {
  var _flowResult$data, _enrollmentResult$dat, _enrollmentResult$dat2, _flowResult$variables, _alerts$enrollmentAle, _alerts$enrollmentAle3;
  const [simulateFlowQuery, flowResult] = useLazyQuery(SIMULATE_FLOW_QUERY, {
    fetchPolicy: 'network-only'
  });
  const [simulateEnrollmentQuery, enrollmentResult] = useLazyQuery(SIMULATE_ENROLLMENT_QUERY, {
    fetchPolicy: 'network-only'
  });
  const isLoading = flowResult.loading || enrollmentResult.loading;
  const [branchIndexOverrides, setBranchIndexOverrides] = useState(ImmutableMap());
  const [shouldOverrideEventEnrollment, setShouldOverrideEventEnrollment] = useState();
  const [activeActionId, setActiveActionId] = useState();

  // Convert branch index overrides to use action IDs as values
  const branchOverrides = useMemo(() => branchIndexOverrides.toSeq().map((branchIndex, actionId) => {
    const connection = flow.getActionById(actionId).getConnection();
    const connector = connection.getConnectorByBranchIndex(branchIndex);
    return connector && connector.getNextActionId();
  }).toJS(), [branchIndexOverrides, flow]);
  const history = useHistory();
  const searchParams = new URLSearchParams(history.location.search);
  const recordId = parseInt(searchParams.get(RECORD_ID_QUERY_PARAM), 10);
  const flowResultData = flowResult.data;
  const historyEvents = useMemo(() => {
    var _flowResultData$SIMUL;
    return (_flowResultData$SIMUL = flowResultData === null || flowResultData === void 0 ? void 0 : flowResultData[SIMULATE_FLOW_QUERY_FIELDNAME].historyEvents) !== null && _flowResultData$SIMUL !== void 0 ? _flowResultData$SIMUL : [];
  }, [flowResultData]);
  const simulatedEnrollmentResult = (_flowResult$data = flowResult.data) === null || _flowResult$data === void 0 ? void 0 : _flowResult$data[SIMULATE_FLOW_QUERY_FIELDNAME].simulatedEnrollmentResult;
  const validationResults = (_enrollmentResult$dat = enrollmentResult.data) === null || _enrollmentResult$dat === void 0 ? void 0 : _enrollmentResult$dat[SIMULATE_ENROLLMENT_QUERY_FIELDNAME].validationResults.filter(validationResult => !VALIDATION_CHECKS_BLOCK_LIST.includes(validationResult.validationType));

  /* cache history events for seamless loading */
  const [historyEventsCache, setHistoryEventsCache] = useState(historyEvents);
  useEffect(() => {
    if (!isLoading) {
      setHistoryEventsCache(historyEvents);
    }
  }, [historyEvents, isLoading]);
  let filteredHistoryEventsCache = historyEventsCache;
  const triggerValidationResults = (_enrollmentResult$dat2 = enrollmentResult.data) === null || _enrollmentResult$dat2 === void 0 ? void 0 : _enrollmentResult$dat2[SIMULATE_ENROLLMENT_QUERY_FIELDNAME].triggerValidationResults.filter(validationResult => !TRIGGER_VALIDATION_CHECKS_BLOCK_LIST.includes(validationResult.enrollmentCategory));
  const enrollType = flowResult === null || flowResult === void 0 || (_flowResult$variables = flowResult.variables) === null || _flowResult$variables === void 0 ? void 0 : _flowResult$variables.enrollType;
  const validationChecks = mapTriggerAndEnrollmentChecksToValidationCheckResults(enrollType === 'DYNAMIC' && triggerValidationResults ? triggerValidationResults : [], validationResults !== null && validationResults !== void 0 ? validationResults : []);
  const alerts = useGetFlowAlerts({
    simulatedEnrollmentResult,
    enrollType,
    validationChecks,
    historyEvents: filteredHistoryEventsCache,
    branchIndexOverrides,
    flow,
    shouldOverrideEventEnrollment,
    setShouldOverrideEventEnrollment,
    recordId,
    isEnrollmentLoading: enrollmentResult.loading
  });
  const simulateFlow = useCallback(args => simulateFlowQuery({
    variables: Object.assign({
      branchOverrides
    }, args)
  }), [simulateFlowQuery, branchOverrides]);
  const simulateEnrollment = useCallback(args => simulateEnrollmentQuery({
    variables: args
  }), [simulateEnrollmentQuery]);
  const startAlert = (_alerts$enrollmentAle = alerts.enrollmentAlert) === null || _alerts$enrollmentAle === void 0 ? void 0 : _alerts$enrollmentAle.alert;
  let actionCardPropsById = ImmutableMap(flow.getActionsAsList().map(action => {
    return [action.actionId, {
      disabled: true,
      cardTheme: 'default'
    }];
  }));
  useEffect(() => {
    setShouldOverrideEventEnrollment(undefined);
    setHistoryEventsCache([]);
  }, [recordId, enrollType]);
  if (!flowResult.loading && !enrollmentResult.loading && flowResult !== null && flowResult !== void 0 && flowResult.data && recordId) {
    var _startAlert$getBanner, _alerts$enrollmentAle2;
    const defaultBannerText = enrollType === 'DYNAMIC' ? I18n.text('automation.testV2.simulatePanel.startAlerts.startedAutomatically') : I18n.text('automation.testV2.simulatePanel.startAlerts.startedManually');
    actionCardPropsById = actionCardPropsById.set('enrollment', {
      borderStyle: startAlert ? startAlert.borderStyle : 'success',
      bannerStyle: startAlert ? startAlert.bannerStyle : 'success',
      bannerText: startAlert ? startAlert === null || startAlert === void 0 || (_startAlert$getBanner = startAlert.getBannerTitle) === null || _startAlert$getBanner === void 0 ? void 0 : _startAlert$getBanner.call(startAlert, {
        enrollType
      }) : defaultBannerText
    });
    alerts.flowAlerts.forEach(flowAlert => {
      if (flowAlert.executionShouldHalt) {
        const lastIndex = filteredHistoryEventsCache.findLastIndex(event => {
          var _event$action;
          return ((_event$action = event.action) === null || _event$action === void 0 ? void 0 : _event$action.actionId) === flowAlert.actionId;
        });
        /* Remove all events after the alert, if the action is present in the history */
        if (lastIndex >= 0) {
          filteredHistoryEventsCache = filteredHistoryEventsCache.slice(0, lastIndex + 1);
        }
      }
    });
    if ((_alerts$enrollmentAle2 = alerts.enrollmentAlert) !== null && _alerts$enrollmentAle2 !== void 0 && (_alerts$enrollmentAle2 = _alerts$enrollmentAle2.alert) !== null && _alerts$enrollmentAle2 !== void 0 && _alerts$enrollmentAle2.executionShouldHalt) {
      filteredHistoryEventsCache = [];
    } else {
      filteredHistoryEventsCache.filter(event => event.type === 'ACTION_EXECUTION').forEach(event => {
        var _branchIndexOverrides;
        if (!event.action) {
          return;
        }
        const action = new Action(event.action);
        const activeBranchIndex = (_branchIndexOverrides = branchIndexOverrides.get(action.actionId)) !== null && _branchIndexOverrides !== void 0 ? _branchIndexOverrides : getFollowedBranchIndex(new LogEvent(event));

        /* Set action card props */
        if (action.actionId) {
          actionCardPropsById = actionCardPropsById.set(event.action.actionId, {
            borderStyle: 'success',
            activeBranchIndex
          });
        }
      });
      alerts.flowAlerts.forEach(flowAlert => {
        if (filteredHistoryEventsCache.find(event => {
          var _event$action2;
          return ((_event$action2 = event.action) === null || _event$action2 === void 0 ? void 0 : _event$action2.actionId) === flowAlert.actionId;
        })) {
          /* Update card borders for alerts that have not been filtered out from event history (due to execution halt for ex) */
          actionCardPropsById = actionCardPropsById.update(flowAlert.actionId, cardProps => {
            return Object.assign({}, cardProps, {
              borderStyle: flowAlert.borderStyle,
              activeBranchIndex: !flowAlert.executionShouldHalt ? cardProps === null || cardProps === void 0 ? void 0 : cardProps.activeBranchIndex : undefined
            });
          });
        }
      });
    }
  }
  const executionShouldHalt = Boolean(alerts.flowAlerts.some(alert => alert.executionShouldHalt) || ((_alerts$enrollmentAle3 = alerts.enrollmentAlert) === null || _alerts$enrollmentAle3 === void 0 || (_alerts$enrollmentAle3 = _alerts$enrollmentAle3.alert) === null || _alerts$enrollmentAle3 === void 0 ? void 0 : _alerts$enrollmentAle3.executionShouldHalt));
  return {
    simulateFlow,
    simulateEnrollment,
    simulatedEnrollmentResult,
    historyEvents: filteredHistoryEventsCache,
    actionCardPropsById,
    recordId,
    validationChecks: [...validationChecks.failed, ...validationChecks.passed],
    enrollType,
    alerts,
    branchOverrides: branchIndexOverrides,
    setBranchOverrides: setBranchIndexOverrides,
    setShouldOverrideEventEnrollment,
    activeActionId,
    setActiveActionId,
    isLoading,
    executionShouldHalt
  };
};
export default useGetTestFlowData;